/**
 * Weekly Agenda Cell Component
 *
 * Renders a single (day, member) cell in the weekly grid.
 * Stacks event cards vertically within the cell.
 *
 * Empty cells respond to a double-tap to create a new event for that day/member.
 *
 * Follows single responsibility: renders one cell with its events.
 */

import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { WeeklyAgendaEventCard } from './WeeklyAgendaEventCard';
import type { WeeklyAgendaCellData } from '@/types/weekly-agenda';
import type { CalendarEvent } from '@/types/events';

type WeeklyAgendaCellProps = {
  cellData: WeeklyAgendaCellData;
  dateKey: string;
  memberColor: string;
  columnWidth: number;
  onEventPress?: (event: CalendarEvent) => void;
  onCreateEvent?: (dateKey: string, memberId: string) => void;
};

export const WeeklyAgendaCell = React.memo(function WeeklyAgendaCell({
  cellData,
  dateKey,
  memberColor,
  columnWidth,
  onEventPress,
  onCreateEvent,
}: WeeklyAgendaCellProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const isEmpty = cellData.events.length === 0;

  const handleCreate = useCallback(() => {
    if (!onCreateEvent) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onCreateEvent(dateKey, cellData.memberId);
  }, [onCreateEvent, dateKey, cellData.memberId]);

  const doubleTapGesture = useMemo(
    () =>
      Gesture.Tap()
        .numberOfTaps(2)
        .onEnd(() => {
          runOnJS(handleCreate)();
        }),
    [handleCreate],
  );

  const cellBody = (
    <View style={[styles.cell, { width: columnWidth, borderRightColor: palette.border }]}>
      {cellData.events.map(event => (
        <WeeklyAgendaEventCard
          key={event.id}
          event={event}
          memberColor={memberColor}
          onPress={onEventPress}
        />
      ))}
    </View>
  );

  if (isEmpty && onCreateEvent) {
    return <GestureDetector gesture={doubleTapGesture}>{cellBody}</GestureDetector>;
  }

  return cellBody;
});

const styles = StyleSheet.create({
  cell: {
    paddingHorizontal: 2,
    paddingVertical: 2,
    minHeight: 48,
    borderRightWidth: 1,
  },
});
